<template>
  <div class="p-4 max-w-xl mx-auto" v-if="!isLoading">
    <div
      @click="$router.back()"
      class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 cursor-pointer shadow-md transition"
    >
      <BackSvg />
    </div>
    <div class="pb-6 flex flex-col justify-center items-center">
      <h2 class="text-lg text-zinc-700 text-center font-semibold">
        Transaction History
      </h2>
    </div>
    <div class="space-y-2">
      <div v-if="!transactions.length">
        <p class="text-sm text-zinc-500">There are no transactions yet.</p>
      </div>
      <div
        v-else
        v-for="(items, date) in groupedTransactions"
        :key="date"
        class="mb-6"
      >
        <h2 class="text-sm font-semibold text-zinc-700 py-3">{{ date }}</h2>
        <ul class="text-gray-600 space-y-2">
          <li class="" v-for="(item, idx) in items" :key="idx">
            <div
              @click="routeDetails(item.transactionid)"
              class="flex justify-between items-center rounded-md p-5 border-neutral-200 shadow-[0_10px_50px_rgba(0,0,0,0.1)] overflow-hidden bg-white"
            >
              <div class="flex gap-3">
                <img
                  class="w-16 h-16 object-cover rounded-full shadow"
                  :src="urlAvatar(item.profile)"
                  alt=""
                />
                <span>
                  <h4 class="text-xs text-zinc-700">
                    #{{ item.transactionid }}
                  </h4>
                  <h3 class="font-medium text-base">{{ item.doctor }}</h3>
                  <h4 class="text-sm text-zinc-400">
                    {{ item.specialization }}
                  </h4>
                </span>
              </div>
              <div
                class="flex justify-center items-center text-xs px-3.5 py-2 rounded-full font-medium"
                :class="statusClass(item.status)"
              >
                {{ modifiedStatus(item.status) }}
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="flex items-center justify-center h-48" v-if="isLoading">
      <svg
        class="w-10 h-10 animate-spin text-orange-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
    </div>
  </div>
</template>
<script>
import { ref, computed, onMounted, inject } from "vue";
import BackSvg from "@/components/svgs/BackSvg.vue";
export default {
  name: "TransactionList",
  components: {
    BackSvg,
  },
  setup() {
    const axios = inject("$axios");
    const moment = inject("$moment");
    const router = inject("$router");
    const isLoading = ref(false);
    const transactions = ref([]);

    const fetchTransactionHistory = async () => {
      try {
        isLoading.value = true;
        const result = await axios.get(`/api/patient/transaction/all`);
        transactions.value = result.data.data.filter((i) => i.doctorid); // ✅ use .data
        isLoading.value = false;
      } catch (error) {
        console.error("Fetch Error:", error);
      }
    };

    onMounted(async () => {
      await fetchTransactionHistory();
    });

    const groupedTransactions = computed(() => {
      const groups = transactions.value.reduce((acc, tx) => {
        const formattedDate = moment(tx.createdat).format("LL"); // e.g., September 4, 2025
        if (!acc[formattedDate]) acc[formattedDate] = [];
        acc[formattedDate].push(tx);
        return acc;
      }, {});

      // 🔹 Sort groups (outer keys) by date DESC
      return Object.fromEntries(
        Object.keys(groups)
          .sort((a, b) => moment(b, "LL").toDate() - moment(a, "LL").toDate())
          .map((createdat) => [
            createdat,
            // 🔹 Sort transactions inside group by createdat DESC
            groups[createdat].sort(
              (tx1, tx2) => new Date(tx2.createdat) - new Date(tx1.createdat)
            ),
          ])
      );
    });

    const urlAvatar = (profile) => {
      return `https://dev-api.iwcsrvr.com/api/portal/patient/profile?filename=${profile}`;
    };
    const routeDetails = (item) => {
      router.push(`/portal/transaction/details/${item}`);
    };
    const modifiedStatus = (status) => {
      let result;
      if (status === "completed") {
        result = "Completed";
      } else if (status === "cancel") {
        result = "Cancel";
      } else if (status === "in-line") {
        result = "Pending";
      } else {
        result = "Denied";
      }

      return result;
    };

    const statusClass = (status) => {
      if (status === "completed") return "bg-green-100 text-green-700";
      if (status === "cancel") return "bg-red-100 text-red-700";
      if (status === "in-line") return "bg-gray-100 text-gray-700";
      return "bg-orange-100 text-orange-700";
    };
    return {
      transactions,
      groupedTransactions,
      fetchTransactionHistory,
      urlAvatar,
      routeDetails,
      isLoading,
      statusClass,
      modifiedStatus,
    };
  },
};
</script>
